/**
 * Inactivity sign-out for the client portal (Story #22576).
 *
 * A signed-in client with no activity for the configured number of minutes is signed out; a
 * warning appears `warningSeconds` beforehand so they can stay (WCAG 2.2.1 Timing Adjustable:
 * warn, and allow at least 20 seconds to extend). Rule: `client-portal-enhancements.md`
 * § Amendment 2026-10-05.
 *
 * Kept free of React so the timing can be tested with fake timers. Cross-tab behaviour uses
 * `localStorage` `storage` events, which every supported browser fires in the OTHER tabs only:
 *  - activity in any tab keeps every tab alive (one shared last-activity time);
 *  - a sign-out in any tab signs every tab out.
 */

/** Fallback when the policy cannot be read: the default, never "no timeout". */
export const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
export const DEFAULT_WARNING_SECONDS = 120;

/** Shared across tabs: the time (ms since epoch) of the last activity anywhere in the portal. */
export const LAST_ACTIVITY_KEY = 'babomrs.client.lastActivity';
/** Written by the tab that signs out; every other tab follows it. */
export const SIGNED_OUT_KEY = 'babomrs.client.idleSignedOut';

/** Pointer, keyboard, touch and scroll — the activity the story counts. */
export const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;

/** Activity is recorded at most this often, so a moving mouse does not write storage 60 times a second. */
const ACTIVITY_THROTTLE_MS = 1000;

export type IdlePhase = 'active' | 'warning' | 'signedOut';

export interface IdleSignOutOptions {
  timeoutMinutes: number;
  warningSeconds: number;
  /** Called once, when this tab must sign out. */
  onSignOut: () => void;
  /** Called on every phase change, and each second while warning, with the seconds left. */
  onChange: (phase: IdlePhase, secondsLeft: number) => void;
  now?: () => number;
  storage?: Storage | null;
  target?: Window;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null; // blocked in some privacy modes: the timer still works for this tab alone
  }
}

/**
 * Starts the inactivity timer and returns its controls. Call `stop()` when the client leaves the
 * signed-in portal.
 */
export function startIdleSignOut(opts: IdleSignOutOptions) {
  const now = opts.now ?? (() => Date.now());
  const storage = opts.storage === undefined ? safeStorage() : opts.storage;
  const target = opts.target ?? window;
  const timeoutMs = Math.max(1, opts.timeoutMinutes) * 60_000;
  // The warning can never start before the session does.
  const warningMs = Math.min(Math.max(0, opts.warningSeconds) * 1000, timeoutMs);

  let lastActivity = now();
  let phase: IdlePhase = 'active';
  let warnTimer: ReturnType<typeof setTimeout> | undefined;
  let endTimer: ReturnType<typeof setTimeout> | undefined;
  let tick: ReturnType<typeof setInterval> | undefined;
  let lastWrite = 0;

  const secondsLeft = () => Math.max(0, Math.ceil((lastActivity + timeoutMs - now()) / 1000));

  function clearTimers() {
    clearTimeout(warnTimer);
    clearTimeout(endTimer);
    clearInterval(tick);
  }

  function setPhase(next: IdlePhase) {
    phase = next;
    opts.onChange(phase, secondsLeft());
  }

  function signOut(broadcast: boolean) {
    if (phase === 'signedOut') return;
    clearTimers();
    detach();
    if (broadcast) {
      try {
        storage?.setItem(SIGNED_OUT_KEY, String(now()));
      } catch {
        /* other tabs will reach their own deadline instead */
      }
    }
    setPhase('signedOut');
    opts.onSignOut();
  }

  function schedule() {
    clearTimers();
    const elapsed = now() - lastActivity;
    const toWarning = timeoutMs - warningMs - elapsed;
    const toEnd = timeoutMs - elapsed;
    if (toEnd <= 0) {
      signOut(true);
      return;
    }
    if (toWarning <= 0) {
      enterWarning();
    } else {
      warnTimer = setTimeout(enterWarning, toWarning);
    }
    endTimer = setTimeout(() => signOut(true), toEnd);
  }

  function enterWarning() {
    if (phase === 'signedOut') return;
    setPhase('warning');
    clearInterval(tick);
    tick = setInterval(() => opts.onChange(phase, secondsLeft()), 1000);
  }

  function recordActivity(at: number, broadcast: boolean, force = false) {
    lastActivity = at;
    if (broadcast && (force || at - lastWrite >= ACTIVITY_THROTTLE_MS)) {
      lastWrite = at;
      try {
        storage?.setItem(LAST_ACTIVITY_KEY, String(at));
      } catch {
        /* this tab still resets; other tabs will not hear about it */
      }
    }
    if (phase !== 'active') setPhase('active');
    schedule();
  }

  /**
   * Ordinary activity in this tab. It does NOT dismiss the warning: once warned, the client must
   * choose — otherwise a stray mouse movement silently closes a dialog they never read.
   */
  function onActivity() {
    if (phase !== 'active') return;
    recordActivity(now(), true);
  }

  function onStorage(e: StorageEvent) {
    if (e.key === SIGNED_OUT_KEY && e.newValue) {
      signOut(false);
      return;
    }
    if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
      const at = Number(e.newValue);
      // Another tab is in use, so this client is not inactive — including while this tab is warning.
      if (Number.isFinite(at) && at > lastActivity) recordActivity(at, false);
    }
  }

  function detach() {
    for (const ev of ACTIVITY_EVENTS) target.removeEventListener(ev, onActivity);
    target.removeEventListener('storage', onStorage);
  }

  for (const ev of ACTIVITY_EVENTS) target.addEventListener(ev, onActivity, { passive: true });
  target.addEventListener('storage', onStorage);
  recordActivity(lastActivity, true);

  return {
    /** "Stay signed in": an explicit choice, so it resets even while warning. */
    staySignedIn() {
      if (phase === 'signedOut') return;
      recordActivity(now(), true, true);
    },
    /** "Sign out now". */
    signOutNow() {
      signOut(true);
    },
    stop() {
      clearTimers();
      detach();
    },
  };
}
